/*
 * Description:
 * ---------------------
 * Every sentence the TAK screen says, in one place.
 *
 * The reader is a signal officer or an operator, not a network engineer, and
 * the first line they see has to answer the only question they came with: is
 * this box showing up on our map. Numbers and addresses come after that, and
 * the terms are TAK's own, because that is the vocabulary they already have.
 *
 * ! file components/takWords.js
 */

/* The headline, answering the question before any detail. Order matters: an
   appliance that is switched on but cannot reach its server is a different
   problem from one nobody switched on, and saying "not on the map" for both
   would send someone to the wrong place. */
export function headline({ enabled, connected, published, last_error: lastError }) {
  if (!enabled) {
    return {
      tone: "off",
      title: "Not on the TAK map",
      detail: "This appliance is not publishing itself to a TAK server. Turn it on below to give it a marker.",
    };
  }
  if (connected) {
    return {
      tone: "good",
      title: "On the TAK map",
      detail: published
        ? "Any TAK client connected to the same server sees this appliance and the state of its circuits."
        : "Connected. The first marker goes out on the next publish.",
    };
  }
  return {
    tone: "bad",
    title: "Switched on, but not reaching the server",
    detail: lastError
      ? `It is retrying. Last attempt: ${lastError}`
      : "It is retrying. Check the server address and that this appliance can reach it.",
  };
}

/* What the marker says about one circuit, in the words the map shows. */
export function circuitLine({ up, carrying_pct: pct }) {
  if (!up) return "down";
  if (!pct) return "up, carrying nothing";
  return `up, carrying ${pct}%`;
}

/* A circuit that is up but carrying nothing is not a fault, and the panel
   should not colour it like one. It usually means the appliance is holding it
   out of the ring after a failure, which is the system working. */
export function circuitTone({ up, carrying_pct: pct }) {
  if (!up) return "down";
  return pct ? "carrying" : "idle";
}

export const POSITION_UNSET =
  "No position set, so the marker carries no accuracy claim. Set one to place it on the map.";

export const TEST_SENT = "Test event sent. It should appear on the map within a few seconds.";

export const FIELD_HELP = {
  host: "The TAK server's address. The Army can host this itself.",
  port: "8087 is the usual port for a plain TCP feed.",
  callsign: "How this appliance is labelled on the map.",
  interval: "How often the marker is refreshed.",
  lat: "Where this appliance is. Leave blank if you would rather not say.",
  lon: "Both are needed to place the marker.",
};

/* The publisher sends over plain TCP only. Saying so here is better than an
   operator discovering it from a refusal after typing an address. */
export const TLS_NOTE =
  "This build publishes over plain TCP. A server that requires certificates is not supported yet.";
